<template>
  <div class="coproducer-products">
    <div class="bg-2 p-2 rounded-8 mb-3 d-flex justify-content-end">
      <snow-search
        class="search-component"
        v-model="search"
        @search="refresh"
      />
    </div>

    <div v-if="loading" class="centering pt-6">
      <b-spinner variant="primary" style="width: 3rem; height: 3rem;" />
    </div>

    <template v-else-if="myCoproducers.length">
      <div v-if="gallery" class="coproduction-cards">
        <product-card
          v-for="coproduction in myCoproducers"
          :key="coproduction.id"
          :id="coproduction.id"
          :created_at="coproduction.created_at"
          :status="coproduction.status"
          :product="coproduction.product"
          @refresh="refresh"
        />
      </div>

      <div v-else>
        <snow-data-table :data="formattedCoproducers" :columns="columns">
          <template #links-de-divulgação="{ value }">
            <snow-button
              variant="borderless"
              size="sm"
              @click="openLinks(value)"
            >
              <i class="ph ph-arrow-square-out" />
            </snow-button>
          </template>
        </snow-data-table>
      </div>

      <div class="d-flex justify-content-end mt-3">
        <bp-paginator
          v-model="page"
          :pagination="pagination"
          @change-page="dispatch"
        />
      </div>
    </template>

    <div v-else class="d-flex justify-content-center w-100">
      <div class="no-content">
        <h2 class="text-center text-semibold ">
          Ainda não há nada aqui
        </h2>
        <img
          class="d-block mx-auto mt-4"
          src="@/assets/images/no-content.svg"
        />
        <hr class="w-100" />
        <h2 class="mb-4">Talvez você precise</h2>

        <div class="bg-2 rounded-16 p-4 w-100 d-flex align-items-start gap-3">
          <i class="ph ph-handshake fs-32" />
          <div class="d-flex flex-column">
            <span class="text-dark text-semibold fs-14"
              >Começar uma parceria de coprodução</span
            >
            <span class="text-dark-40 fs-14"
              >Contacte seu parceiro de vendas para cadastrá-lo como
              coprodutor</span
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import ProductCard from "./CoproducerProductCard";
import SnowSearch from "@/components/snow-ui/SnowSearch.vue";
import SnowDataTable from "@/components/snow-ui/dataTable/SnowDataTable.vue";
import { clearObject } from "@/functions/helpers";
import { mapState } from "vuex";
import moment from "moment";

export default {
  components: { ProductCard, SnowSearch, SnowDataTable },
  data() {
    return {
      loading: false,
      page: 1,
      search: ""
    };
  },
  computed: {
    ...mapState("user", ["preferences", "myCoproducers", "pagination"]),
    gallery() {
      return this.preferences.product_list === "gallery";
    },
    formattedCoproducers() {
      return this.myCoproducers.map(coproducer => ({
        id: coproducer.id,
        productName: coproducer.product.name,
        productType: coproducer.product.type_product,
        createdAt: coproducer.created_at,
        status: coproducer.status,
        product_id: coproducer.product.id
      }));
    },
    columns() {
      return [
        {
          name: "Produto",
          field: "productName"
        },
        {
          name: "Tipo de produto",
          field: "productType",
          transform: this.getProductType
        },
        {
          name: "Links de divulgação",
          field: "product_id"
        },
        {
          name: "Data de criação",
          field: "createdAt",
          transform: this.formatDate
        },
        {
          name: "Status",
          field: "status",
          transform: this.formatStatus,
          color: v =>
            ({
              active: "green",
              cancelled: "negative",
              rejected: "negative",
              waiting_validation: "alert",
              pendent: "alert"
            }[v]),
          type: "tag"
        },
        {
          type: "menu",
          disabled: coproduction => coproduction.status === "cancelled",
          options: coproduction =>
            coproduction.status === "cancelled"
              ? []
              : [
                  {
                    label: "Cancelar contrato",
                    dialog: {
                      text: "Tem certeza que deseja cancelar o contrato?",
                      variant: "negative",
                      icon: "fa fa-hexagon-exclamation",
                      buttonText: "Cancelar",
                      secondaryButtonText: "Confirmar",
                      secondaryAction: coproducion =>
                        this.cancelContract(coproducion.id)
                    }
                  }
                ]
        }
      ];
    }
  },
  methods: {
    refresh() {
      this.page = 1;
      this.dispatch();
    },
    dispatch(offset) {
      this.loading = true;

      this.$store
        .dispatch(
          "user/getMyCoproducer",
          clearObject({ offset, search: this.search })
        )
        .catch(() => {
          this.$message.error("Erro ao recuperar produtos");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formatDate(date) {
      if (!date) return "-";
      return (
        moment(date).format("DD/MM/YYYY") +
        " às " +
        moment(date).format("HH:mm")
      );
    },
    getProductType(productType) {
      const options = {
        online_course: "Curso online",
        signature: "Assinatura",
        onsite_event: "Evento"
      };
      return options[productType] || productType;
    },
    formatStatus(status) {
      const options = {
        active: "Ativo",
        cancelled: "Cancelado",
        rejected: "Rejeitado",
        waiting_validation: "Aguardando validação",
        pendent: "Pendente"
      };
      return options[status] || status;
    },
    cancelContract(coproductionId) {
      this.loading = true;
      this.$store
        .dispatch("user/updateMyCoproducer", {
          id: coproductionId,
          status: "cancelled"
        })
        .then(() => {
          this.refresh();
          this.$message.success("Contrato cancelado com sucesso");
        })
        .catch(() => {
          this.$message.error("Erro ao cancelar contrato");
        });
    },
    openLinks(product_id) {
      this.$router.push({
        name: "coproducer-product-links",
        params: { id: product_id }
      });
    }
  },
  mounted() {
    this.dispatch();
  }
};
</script>

<style lang="scss" scoped>
.no-content {
  display: flex;
  width: 100%;
  margin-top: 100px;
  max-width: 380px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}
.coproduction-cards {
  gap: 12px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  transition: grid-template-columns 0.3s;
}
.search-component {
  max-width: 160px;

  @media screen and (max-width: 991px) {
    max-width: 100%;
  }
}
</style>
